import { ethers } from 'ethers'
import {
  AERO_POOL_FACTORY,
  CL3_FACTORY,
  CL_FACTORY,
  CL_TICK_SPACINGS_F1,
  CL_TICK_SPACINGS_F3,
  MULTICALL3,
  UNI_V2_FACTORY,
  UNI_V3_FACTORY,
  UNI_V3_FEES
} from './addresses'
import {
  AERO_POOL_FACTORY_ABI,
  CL_FACTORY_ABI,
  MPRO_DISTRIBUTOR_ABI,
  MULTICALL3_ABI,
  UNI_V2_FACTORY_ABI,
  UNI_V3_FACTORY_ABI
} from './abi'
import type { OnChainVenue, PoolRef, SwapToken } from './types'

/**
 * Which pools exist for a pair, asked in one round trip.
 *
 * A factory answers `getPool` for a combination that was never created with
 * the zero address rather than a revert, so the whole question — direct pair
 * and every hub leg, across five venues and every fee tier — is one
 * `aggregate3` of about 260 rows. That is one RPC call for what would
 * otherwise be two hundred and sixty, which is the difference between a page
 * that quotes and a page that rate-limits the proxy.
 */

const AERO_STABLE_FLAGS = [false, true] as const

export interface DiscoveryCall {
  target: string
  allowFailure: true
  callData: string
}

interface CallDescriptor {
  venue: OnChainVenue
  tokenA: string
  tokenB: string
  stable?: boolean
  fee?: number
  tickSpacing?: number
}

export interface DiscoveryPlan {
  calls: DiscoveryCall[]
  /** Turns the multicall rows back into pools, grouped by pair. */
  decode(results: ReadonlyArray<{ success: boolean; returnData: string }>): Map<string, PoolRef[]>
}

const aeroFactoryIface = new ethers.utils.Interface(AERO_POOL_FACTORY_ABI as unknown as string[])
const clFactoryIface = new ethers.utils.Interface(CL_FACTORY_ABI as unknown as string[])
const uniV3FactoryIface = new ethers.utils.Interface(UNI_V3_FACTORY_ABI as unknown as string[])
const uniV2FactoryIface = new ethers.utils.Interface(UNI_V2_FACTORY_ABI as unknown as string[])
const multicallIface = new ethers.utils.Interface(MULTICALL3_ABI as unknown as string[])

/** Pairs are unordered; the key sorts so `{a,b}` and `{b,a}` are one entry. */
export function pairKey(a: string, b: string): string {
  const [x, y] = [a.toLowerCase(), b.toLowerCase()].sort()
  return `${x}:${y}`
}

/**
 * Every pair worth probing: the direct one, and both legs through each hub
 * that is not already one of the two tokens. At most `1 + 2·|hubs|`.
 */
export function discoveryPairs(tokenIn: string, tokenOut: string, hubs: string[]): Array<[string, string]> {
  const inAddr = tokenIn.toLowerCase()
  const outAddr = tokenOut.toLowerCase()
  const seen = new Set<string>()
  const pairs: Array<[string, string]> = []
  const push = (a: string, b: string) => {
    if (a === b) return
    const key = pairKey(a, b)
    if (seen.has(key)) return
    seen.add(key)
    pairs.push([a, b])
  }
  push(inAddr, outAddr)
  for (const hub of hubs.map((h) => h.toLowerCase())) {
    if (hub === inAddr || hub === outAddr) continue
    push(inAddr, hub)
    push(hub, outAddr)
  }
  return pairs
}

export function buildDiscoveryCalls(tokenIn: string, tokenOut: string, hubs: string[]): DiscoveryPlan {
  const calls: DiscoveryCall[] = []
  const descriptors: CallDescriptor[] = []

  for (const [a, b] of discoveryPairs(tokenIn, tokenOut, hubs)) {
    for (const stable of AERO_STABLE_FLAGS) {
      calls.push({
        target: AERO_POOL_FACTORY,
        allowFailure: true,
        callData: aeroFactoryIface.encodeFunctionData('getPool', [a, b, stable])
      })
      descriptors.push({ venue: 'aero-v2', tokenA: a, tokenB: b, stable })
    }
    for (const tickSpacing of CL_TICK_SPACINGS_F1) {
      calls.push({
        target: CL_FACTORY,
        allowFailure: true,
        callData: clFactoryIface.encodeFunctionData('getPool', [a, b, tickSpacing])
      })
      descriptors.push({ venue: 'aero-cl', tokenA: a, tokenB: b, tickSpacing })
    }
    for (const tickSpacing of CL_TICK_SPACINGS_F3) {
      calls.push({
        target: CL3_FACTORY,
        allowFailure: true,
        callData: clFactoryIface.encodeFunctionData('getPool', [a, b, tickSpacing])
      })
      descriptors.push({ venue: 'aero-cl3', tokenA: a, tokenB: b, tickSpacing })
    }
    for (const fee of UNI_V3_FEES) {
      calls.push({
        target: UNI_V3_FACTORY,
        allowFailure: true,
        callData: uniV3FactoryIface.encodeFunctionData('getPool', [a, b, fee])
      })
      descriptors.push({ venue: 'uni-v3', tokenA: a, tokenB: b, fee })
    }
    calls.push({
      target: UNI_V2_FACTORY,
      allowFailure: true,
      callData: uniV2FactoryIface.encodeFunctionData('getPair', [a, b])
    })
    descriptors.push({ venue: 'uni-v2', tokenA: a, tokenB: b })
  }

  const decode = (
    results: ReadonlyArray<{ success: boolean; returnData: string }>
  ): Map<string, PoolRef[]> => {
    const byPair = new Map<string, PoolRef[]>()
    for (let i = 0; i < descriptors.length; i++) {
      const row = results[i]
      if (!row || !row.success) continue
      let address: string
      try {
        address = String(ethers.utils.defaultAbiCoder.decode(['address'], row.returnData)[0]).toLowerCase()
      } catch {
        continue
      }
      if (address === ethers.constants.AddressZero) continue
      const d = descriptors[i]
      const [token0, token1] = [d.tokenA, d.tokenB].sort()
      const pool: PoolRef = {
        venue: d.venue,
        address,
        token0,
        token1,
        ...(d.stable === undefined ? {} : { stable: d.stable }),
        ...(d.fee === undefined ? {} : { fee: d.fee }),
        ...(d.tickSpacing === undefined ? {} : { tickSpacing: d.tickSpacing })
      }
      const key = pairKey(d.tokenA, d.tokenB)
      const list = byPair.get(key)
      if (list) list.push(pool)
      else byPair.set(key, [pool])
    }
    return byPair
  }

  return { calls, decode }
}

/**
 * MPRO burns a share of every transfer whose sender is not whitelisted, and
 * the concentrated-liquidity routers cannot deliver a token that arrives
 * smaller than it was sent. Dropping those pools here is what keeps the page
 * from ranking a route that is guaranteed to revert above one that works.
 *
 * At burn 0 — which is the live value — nothing is dropped.
 */
export function filterPoolsForBurn(
  byPair: Map<string, PoolRef[]>,
  mproAddress: string | null,
  burnBps: number
): Map<string, PoolRef[]> {
  if (!mproAddress || burnBps <= 0) return byPair
  const mpro = mproAddress.toLowerCase()
  const out = new Map<string, PoolRef[]>()
  for (const [key, pools] of byPair) {
    const kept = pools.filter((p) => {
      const touchesMpro = p.token0 === mpro || p.token1 === mpro
      const feeOnTransferHostile =
        p.venue === 'aero-cl' || p.venue === 'aero-cl3' || p.venue === 'uni-v3'
      return !(touchesMpro && feeOnTransferHostile)
    })
    if (kept.length > 0) out.set(key, kept)
  }
  return out
}

/**
 * How much of a transfer from `account` MPRO would burn, in basis points.
 *
 * Asked per account rather than read from `burnRate()`, because a whitelisted
 * sender burns nothing whatever the global rate says — the same reason the
 * marketplace reads it per seller.
 */
export async function readMproBurnBps(
  provider: ethers.providers.Provider,
  distributorAddress: string,
  account: string | null | undefined
): Promise<number> {
  try {
    const distributor = new ethers.Contract(
      distributorAddress,
      MPRO_DISTRIBUTOR_ABI as unknown as ethers.ContractInterface,
      provider
    )
    if (account) {
      const burned: ethers.BigNumber = await distributor.getBurnAmount(account, 10000)
      return burned.toNumber()
    }
    const rate: ethers.BigNumber = await distributor.burnRate()
    return rate.toNumber()
  } catch {
    /*
     * A distributor we cannot read is not a reason to hide half the venues.
     * Reporting no burn keeps every route visible; a route that then cannot
     * deliver reverts in the dry run and is explained there, which is the
     * failure the user can actually act on.
     */
    return 0
  }
}

interface CacheEntry {
  at: number
  pools: Map<string, PoolRef[]>
}

const DISCOVERY_TTL_MS = 10 * 60 * 1000
const discoveryCache = new Map<string, CacheEntry>()

export function clearDiscoveryCache(): void {
  discoveryCache.clear()
}

function cacheKey(chainId: number, tokenIn: string, tokenOut: string, hubs: string[]): string {
  return `${chainId}:${pairKey(tokenIn, tokenOut)}:${hubs.length}`
}

/**
 * Discovers every pool for a pair and its hub legs. Pools appear rarely, so
 * the result is held for ten minutes; the page's refresh control clears it.
 */
export async function discoverPools(params: {
  provider: ethers.providers.Provider
  chainId: number
  tokenIn: SwapToken
  tokenOut: SwapToken
  hubs: string[]
  now?: number
}): Promise<Map<string, PoolRef[]>> {
  const { provider, chainId, tokenIn, tokenOut, hubs } = params
  const now = params.now ?? Date.now()
  const key = cacheKey(chainId, tokenIn.address, tokenOut.address, hubs)
  const cached = discoveryCache.get(key)
  if (cached && now - cached.at < DISCOVERY_TTL_MS) return cached.pools

  const plan = buildDiscoveryCalls(tokenIn.address, tokenOut.address, hubs)
  const multicall = new ethers.Contract(
    MULTICALL3,
    multicallIface as unknown as ethers.ContractInterface,
    provider
  )
  const raw: Array<{ success: boolean; returnData: string }> = await multicall.callStatic.aggregate3(plan.calls)
  const pools = plan.decode(raw)
  discoveryCache.set(key, { at: now, pools })
  return pools
}
